import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <div data-testid="home-page" className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img
            src="/images/5097v5ro_7A161535-CDD8-49CF-8EF3-45305B6FCC46.JPG"
            alt="Riad KAMA Patio"
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
        </motion.div>

        <div className="relative z-10 text-center px-4 sm:px-8 max-w-7xl mt-12 sm:mt-16">
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#D4AF37] text-xs sm:text-sm md:text-base uppercase tracking-[0.2em] sm:tracking-[0.4em] mb-4 sm:mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
          >
            {t('home.exclusive')}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-3xl sm:text-5xl md:text-7xl lg:text-8xl text-white mb-6 sm:mb-10 leading-[1.15]"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
            data-testid="hero-title"
          >
            {t('home.title1')}<br/>
            {t('home.title2')}<br/>
            {t('home.title3')}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-4 sm:gap-8 mb-10 sm:mb-16 max-w-4xl mx-auto"
          >
            <div className="text-center px-2">
              <div className="text-2xl sm:text-4xl font-serif text-[#D4AF37] mb-1">{t('home.stat1')}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/70">{t('home.stat1Label')}</div>
            </div>
            <div className="hidden sm:block w-[1px] bg-[#D4AF37]/30"></div>
            <div className="text-center px-2">
              <div className="text-2xl sm:text-4xl font-serif text-[#D4AF37] mb-1">{t('home.stat2')}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/70">{t('home.stat2Label')}</div>
            </div>
            <div className="hidden sm:block w-[1px] bg-[#D4AF37]/30"></div>
            <div className="text-center px-2">
              <div className="text-2xl sm:text-4xl font-serif text-[#D4AF37] mb-1">{t('home.stat3')}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/70">{t('home.stat3Label')}</div>
            </div>
            <div className="hidden sm:block w-[1px] bg-[#D4AF37]/30"></div>
            <div className="text-center px-2">
              <div className="text-2xl sm:text-4xl font-serif text-[#D4AF37] mb-1">{t('home.stat4')}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/70">{t('home.stat4Label')}</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4"
          >
            <button
              onClick={() => navigate('/disponibilites')}
              data-testid="cta-book-button"
              className="gold-gradient text-[#0A0A0A] w-full sm:w-auto px-8 sm:px-14 py-4 sm:py-5 hover:shadow-2xl transition-all duration-700 uppercase tracking-[0.15em] sm:tracking-[0.25em] text-xs font-semibold group flex items-center justify-center gap-3"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {t('home.bookNow')}
              <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform duration-500" />
            </button>
            <button
              onClick={() => navigate('/le-riad')}
              data-testid="cta-availability-button"
              className="border-2 border-[#D4AF37] text-[#D4AF37] w-full sm:w-auto px-8 sm:px-14 py-4 sm:py-5 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all duration-700 uppercase tracking-[0.15em] sm:tracking-[0.25em] text-xs font-semibold"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {t('home.discover')}
            </button>
          </motion.div>
        </div>

        {/* Scroll indicator - hidden on mobile */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 1.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden sm:block"
        >
          <div className="flex flex-col items-center gap-2">
            <div className="w-[1px] h-12 bg-gradient-to-b from-[#D4AF37] to-transparent animate-pulse"></div>
          </div>
        </motion.div>
      </section>

      {/* Introduction Section */}
      <section className="py-32 md:py-48 px-8 md:px-16 lg:px-32 bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-24">
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              L'expérience Riad KAMA
            </p>
            <h2 className="font-serif text-4xl md:text-6xl text-[#1A1A1A] mb-12" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
              L'art de vivre marocain,<br/>réinventé pour vous
            </h2>
            <div className="max-w-4xl mx-auto space-y-8 text-lg md:text-xl text-[#1A1A1A]/80 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
              <p>
                Niché au fond d'un derb calme du quartier historique de la Kasbah, le Riad KAMA est une demeure d'exception entièrement privatisée qui célèbre l'élégance intemporelle de l'architecture marocaine.
              </p>
              <p>
                Récemment rénové avec un souci du détail obsessionnel, ce riad de 400 m² offre une expérience unique où l'authenticité ancestrale rencontre le confort moderne. Chaque espace a été pensé pour créer une atmosphère intime et apaisante, loin de l'agitation de la médina.
              </p>
              <p className="text-[#B8860B] text-2xl font-medium">
                Que vous soyez 2 ou 10 personnes, le riad vous est entièrement dédié. Aucun autre voyageur ne partagera ce lieu avec vous. C'est votre maison privée à Marrakech.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <motion.div {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.2 }} className="text-center bg-white/60 backdrop-blur-sm p-10 border border-[#D4AF37]/30 shadow-sm">
              <div className="text-6xl font-serif text-[#B8860B] mb-4">10</div>
              <p className="text-sm uppercase tracking-[0.2em] text-[#1A1A1A] mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Personnes maximum
              </p>
              <p className="text-sm text-[#1A1A1A]/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Idéal pour familles et groupes d'amis
              </p>
            </motion.div>
            <motion.div {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.3 }} className="text-center bg-white/60 backdrop-blur-sm p-10 border border-[#D4AF37]/30 shadow-sm">
              <div className="text-6xl font-serif text-[#B8860B] mb-4">5</div>
              <p className="text-sm uppercase tracking-[0.2em] text-[#1A1A1A] mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Chambres élégantes
              </p>
              <p className="text-sm text-[#1A1A1A]/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Chacune avec sa propre salle de bain
              </p>
            </motion.div>
            <motion.div {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.4 }} className="text-center bg-white/60 backdrop-blur-sm p-10 border border-[#D4AF37]/30 shadow-sm">
              <div className="text-6xl font-serif text-[#B8860B] mb-4">400</div>
              <p className="text-sm uppercase tracking-[0.2em] text-[#1A1A1A] mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                m² d'espace
              </p>
              <p className="text-sm text-[#1A1A1A]/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Entre tradition et modernité
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-32 md:py-48 px-8 md:px-16 lg:px-32 bg-[#0A0A0A]" data-testid="gallery-section">
        <div className="max-w-[1800px] mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-20">
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Galerie
            </p>
            <h2 className="font-serif text-4xl md:text-6xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
              Un voyage visuel
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-6 h-[1200px]">
            <motion.div {...fadeInUp} className="md:col-span-2 md:row-span-2 relative overflow-hidden group border border-[#D4AF37]/20">
              <img
                src="/images/rey0acg7_616FD1E8-33A4-4E04-A26C-D74C9EE4E5BC.JPG"
                alt="Salle à manger"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s] ease-out"
              />
            </motion.div>

            <motion.div {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.2 }} className="md:col-span-1 relative overflow-hidden group border border-[#D4AF37]/20">
              <img
                src="/images/17wjim7n_17CCA6B1-4307-4C0A-B62A-6C7BDC845220.JPG"
                alt="Fontaine"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s] ease-out"
              />
            </motion.div>

            <motion.div {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.3 }} className="md:col-span-1 relative overflow-hidden group border border-[#D4AF37]/20">
              <img
                src="/images/gjkmwhbz_058F89B8-42D5-40E8-B6D7-CA0F6ABD5A4F.JPG"
                alt="Petit déjeuner rooftop"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s] ease-out"
              />
            </motion.div>

            <motion.div {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.4 }} className="md:col-span-2 relative overflow-hidden group border border-[#D4AF37]/20">
              <img
                src="/images/i7m3l513_8693075F-13C8-4725-A3D8-3C6B4C460578.JPG"
                alt="Rooftop avec bassin"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s] ease-out"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-48 px-8 md:px-16 lg:px-32 bg-[#FAF8F5] text-center" data-testid="cta-section">
        <div className="max-w-5xl mx-auto">
          <motion.p {...fadeInUp} className="text-[#B8860B] text-xs uppercase tracking-[0.3em] mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Prêt pour l'expérience ?
          </motion.p>

          <motion.h2 {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.2 }} className="font-serif text-5xl md:text-7xl mb-12 text-[#1A1A1A]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
            Réservez votre séjour d'exception
          </motion.h2>

          <motion.p {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.3 }} className="text-xl text-[#1A1A1A]/70 mb-16 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
            Le Riad KAMA vous attend pour un séjour inoubliable dans l'intimité et le luxe de la Kasbah. Consultez nos disponibilités et contactez-nous pour un devis personnalisé.
          </motion.p>

          <motion.button {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.4 }} onClick={() => navigate('/disponibilites')} data-testid="discover-button" className="gold-gradient text-[#0A0A0A] px-16 py-6 hover:shadow-2xl transition-all duration-700 uppercase tracking-[0.2em] text-xs font-medium inline-flex items-center gap-3 group" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Consulter les disponibilités
            <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform duration-500" />
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default Home;
